import { useEffect, useMemo, useState } from 'react';
import { FiTrash2, FiX } from 'react-icons/fi';
import type { BoxDraft } from '../types';
import { SPLITTER_OPTIONS, STATUS_OPTIONS, TYPE_OPTIONS, parseCoord } from '../lib/boxes';
import { Button, ModalBackdrop, Select, TextInput, inputClass } from './ui';
import { cn } from '../lib/cn';
import { useModalKeys } from '../../../hooks/useModalKeys';

interface BoxModalProps {
  box: BoxDraft;
  isAdmin: boolean;
  popOptions: string[];
  onClose: () => void;
  onSave: (payload: BoxDraft) => void;
  onDelete?: (id: string) => void;
}

export default function BoxModal({ box, isAdmin, popOptions, onClose, onSave, onDelete }: BoxModalProps) {
  const [draft, setDraft] = useState<BoxDraft | null>(null);
  const [viewerUri, setViewerUri] = useState<string | null>(null);
  const [servico, setServico] = useState('Manutenção');

  const isMounted = !!(box.montadoPor && String(box.montadoPor).trim());

  const modalPops = useMemo(() => {
    const values = new Set((popOptions || []).filter(Boolean).map((it) => String(it).trim()));
    if (draft?.pop) values.add(String(draft.pop).trim());
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [draft?.pop, popOptions]);

  const addImages = async (files: FileList | null) => {
    const arr = Array.from(files || []);
    const images = await Promise.all(
      arr.map(
        (f) =>
          new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result || ''));
            r.onerror = reject;
            r.readAsDataURL(f);
          })
      )
    );
    setDraft((p) => (p ? { ...p, images: [...p.images, ...images.filter(Boolean)] } : p));
  };

  useEffect(() => {
    if (!box) return;
    setDraft({ ...box });
    setServico('Manutenção');
  }, [box]);

  if (!draft) return null;

  const setField = (k: keyof BoxDraft, v: string) => setDraft((p) => (p ? { ...p, [k]: v } : p));

  const fieldLabel = 'text-xs font-semibold text-slate-600';

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-bold text-slate-800">{box.originalId ? 'Detalhes da Caixa' : 'Nova Caixa'}</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isMounted && (
            <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              <span className="font-semibold">🔧 Montada por:</span>
              <span className="font-medium">{box.montadoPor}</span>
              {box.montadoEm && (
                <span className="text-emerald-600">· {new Date(box.montadoEm).toLocaleDateString('pt-BR')}</span>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={fieldLabel}>Nome</label>
              <TextInput disabled={!isAdmin} value={draft.name} onChange={(e) => setField('name', e.target.value)} />
            </div>
            <div>
              <label className={fieldLabel}>ID</label>
              <TextInput disabled={!isAdmin} value={draft.id} onChange={(e) => setField('id', e.target.value)} />
            </div>
            <div>
              <label className={fieldLabel}>Tipo</label>
              <Select disabled={!isAdmin} value={draft.type} onChange={(e) => setField('type', e.target.value)}>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className={fieldLabel}>Splitter</label>
              <Select disabled={!isAdmin} value={draft.splitter || ''} onChange={(e) => setField('splitter', e.target.value)}>
                {SPLITTER_OPTIONS.map((o) => (
                  <option key={o || 'none'} value={o}>
                    {o || 'Nenhum'}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className={fieldLabel}>POP</label>
              <Select disabled={!isAdmin} value={draft.pop || ''} onChange={(e) => setField('pop', e.target.value)}>
                <option value="">Nenhum</option>
                {modalPops.map((pop) => (
                  <option key={pop} value={pop}>
                    {pop}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className={fieldLabel}>Latitude</label>
              <TextInput
                disabled={!isAdmin}
                value={String(draft.latitude ?? '')}
                onChange={(e) => setField('latitude', e.target.value)}
              />
            </div>
            <div>
              <label className={fieldLabel}>Longitude</label>
              <TextInput
                disabled={!isAdmin}
                value={String(draft.longitude ?? '')}
                onChange={(e) => setField('longitude', e.target.value)}
              />
            </div>
            <div>
              <label className={fieldLabel}>Sinal</label>
              <TextInput value={draft.signal || ''} onChange={(e) => setField('signal', e.target.value)} />
            </div>
            <div>
              <label className={fieldLabel}>Status</label>
              <Select value={draft.status || ''} onChange={(e) => setField('status', e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o || 'empty'} value={o}>
                    {o || 'vazio'}
                  </option>
                ))}
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className={fieldLabel}>Serviço</label>
              <Select value={servico} onChange={(e) => setServico(e.target.value)}>
                <option value="Montagem" disabled={isMounted}>
                  Montagem{isMounted ? ' (já montada)' : ''}
                </option>
                <option value="Manutenção">Manutenção</option>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <label className={fieldLabel}>
              Imagens{' '}
              <span className={cn('font-normal', isAdmin || (draft.images || []).length >= 2 ? 'text-slate-400' : 'text-red-500')}>
                ({isAdmin ? '' : 'mínimo 2 — '}
                {(draft.images || []).length} anexada{(draft.images || []).length === 1 ? '' : 's'})
              </span>
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => addImages(e.target.files)}
              className={cn(
                'mt-1 w-full text-sm text-slate-500',
                'file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200'
              )}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {(draft.images || []).map((uri) => (
                <div key={uri} className="group relative">
                  <img
                    src={uri}
                    alt="img"
                    onClick={() => setViewerUri(uri)}
                    className="h-20 w-20 cursor-zoom-in rounded-lg border border-slate-200 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setDraft((p) => (p ? { ...p, images: p.images.filter((x) => x !== uri) } : p))}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white shadow hover:bg-red-700"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          {isAdmin && onDelete && box.originalId && (
            <Button
              variant="danger"
              onClick={() => {
                if (window.confirm(`Excluir a caixa "${draft.name}" (${draft.id})?\n\nEsta ação não pode ser desfeita.`)) {
                  onDelete(box.originalId!);
                }
              }}
            >
              <FiTrash2 size={16} /> Excluir caixa
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const latitude = parseCoord(draft.latitude);
              const longitude = parseCoord(draft.longitude);
              if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                window.alert('Latitude/Longitude invalidas.');
                return;
              }
              if (!isAdmin && (draft.images || []).length < 2) {
                window.alert('Anexe ao menos 2 fotos para salvar a caixa.');
                return;
              }
              onSave({
                ...draft,
                latitude,
                longitude,
                servico,
                id: isAdmin ? String(draft.id || '').trim() : box.id,
                name: isAdmin ? String(draft.name || '').trim() : box.name,
                type: isAdmin ? draft.type : box.type,
                splitter: isAdmin ? draft.splitter : box.splitter,
                pop: isAdmin ? String(draft.pop || '').trim() : box.pop
              });
            }}
          >
            Salvar
          </Button>
        </div>
      </div>

      {viewerUri && <ImageViewer uri={viewerUri} onClose={() => setViewerUri(null)} />}
    </ModalBackdrop>
  );
}

/**
 * Visualizador de imagem em tela cheia. Empilha sobre o modal — o Esc
 * fecha só o visualizador (a pilha do useModalKeys cuida da ordem).
 */
function ImageViewer({ uri, onClose }: { uri: string; onClose: () => void }) {
  useModalKeys(onClose);
  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/85 p-6"
      onClick={onClose}
    >
      <img
        src={uri}
        alt="visualizar"
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded-lg object-contain"
      />
      <button
        onClick={onClose}
        className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-xl text-white hover:bg-white/25"
      >
        ✕
      </button>
    </div>
  );
}
